import { omit } from 'lodash';
import { v4 as uuid } from 'uuid';
import { ITEM_READONLY_FIELDS } from '@onward-delivery/core';
import { palletSourceOf } from '../../../utilities/pallets';

// Fields the mobile pick writes onto a line. A modal opened before the pick holds stale values for
// them, so they're written only when the user changed them, through a targeted update.
export const PICK_OWNED_ITEM_FIELDS = ['serial_number', 'barcode'];

const normalizeItemValue = (value) => {
    if (value === undefined || value === null) return null;
    if (typeof value === 'string') return value.trim() || null;
    return value;
};

const byItemId = (items) => new Map(items.filter((item) => item?.item_id).map((item) => [item.item_id, item]));

// An existing row's pallet_id is never upserted, so the link to a pallet the user declared on it is
// written by a targeted update, and only when the declared pallet differs from the one it had.
export const buildItemUpdates = ({ originalItems = [], editedItems = [] }) => {
    const originalById = byItemId(originalItems);

    return editedItems.flatMap((item) => {
        const before = item?.item_id ? originalById.get(item.item_id) : null;
        if (!before) return [];

        const _set = Object.fromEntries(
            PICK_OWNED_ITEM_FIELDS.filter(
                (field) => normalizeItemValue(item[field]) !== normalizeItemValue(before[field])
            ).map((field) => [field, normalizeItemValue(item[field])])
        );

        const declaredPalletId = palletSourceOf(item)?.pallet_id ?? null;
        if (declaredPalletId && declaredPalletId !== (before.pallet_id ?? null)) {
            _set.pallet_id = declaredPalletId;
        }

        if (!Object.keys(_set).length) return [];

        return [{ where: { item_id: { _eq: item.item_id } }, _set }];
    });
};

// A pallet newly declared on an existing row gets its id here, so the nested pallet insert and the
// targeted pallet_id update agree on it. New rows link through the nested insert instead.
const withDeclaredPalletIds = ({ originalItems, editedItems }) => {
    const originalById = byItemId(originalItems);

    return editedItems.map((item) => {
        const pallet = palletSourceOf(item);
        if (!pallet || pallet.pallet_id || !originalById.has(item?.item_id)) return item;
        return { ...item, pallet: { ...pallet, pallet_id: uuid() } };
    });
};

// `items` stays the full item list: callers that don't persist through UPSERT_ORDERS treat it as the
// order's lines.
export const buildItemsSavePayload = ({ originalItems = [], editedItems = [] }) => {
    const safeItems = withDeclaredPalletIds({ originalItems, editedItems });

    const palletNameCount = safeItems.reduce((acc, item) => {
        const pallet_name = palletSourceOf(item)?.pallet_name;
        if (pallet_name) {
            acc[pallet_name] = (acc[pallet_name] || 0) + 1;
        }
        return acc;
    }, {});

    const items = safeItems.map((item) => {
        const pallet = palletSourceOf(item);
        // pallet_id is dropped from the item itself; the nested pallet insert below owns the
        // relationship on new rows.
        const rest = omit(item, [...ITEM_READONLY_FIELDS, 'pallet_id']);

        return {
            ...rest,
            ...(pallet
                ? {
                      pallet: {
                          data: {
                              pallet_name: pallet.pallet_name,
                              pallet_id: pallet.pallet_id,
                              // Read-only here: never in on_conflict.update_columns, so it can only
                              // ever set the column on a fresh insert. The ShipmentForm submit
                              // boundary needs it to tell a locked pallet from a pre-receipt one.
                              ...(pallet.warehouse_status ? { warehouse_status: pallet.warehouse_status } : {}),
                              type: palletNameCount[pallet.pallet_name] > 1 ? 'CONSOLIDATED' : 'SINGLE',
                          },
                          on_conflict: {
                              constraint: 'pallet_items_pkey',
                              update_columns: ['pallet_name', 'type'],
                          },
                      },
                  }
                : {}),
        };
    });

    return { items, itemUpdates: buildItemUpdates({ originalItems, editedItems: safeItems }) };
};
